import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Form,
  Select,
  DatePicker,
  Button,
  Spin,
  Alert,
  message,
} from 'antd';
import { SearchOutlined, HistoryOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { logger } from '@/shared/lib/logger';
import { RESPONSIVE_STYLES } from '@/shared/constants/breakpoints';
import { DateFactory } from '@/shared/utils/dateFactory';
import { OperationPeriodService } from '@/features/operation-periods/services/operationPeriodService';
import { OperationPeriodTable } from '@/features/operation-periods/components/OperationPeriodTable';
import { OperationChartModal } from '@/features/operation-periods/components/OperationChartModal';
import { getAllPanels } from '@/features/panels/services/panelApiService';
import type { PanelBasicInfo } from '@/features/panels/services/panelApiService';
import type { OperationPeriod } from '@/features/operation-periods/types/operation-period.types';

// ========== 定数定義 ==========
const HISTORY_DATA_CONSTANTS = {
  DEFAULT_DATE_RANGE_DAYS: 7,
  DECIMAL_BASE: 10,
} as const;

// ========== 型定義 ==========
// UI層ではプリミティブ値で状態管理（サービス呼び出し時にサービス内部でドメインオブジェクトに変換）
interface SearchFilter {
  readonly panelId: number | undefined;
  readonly dateRange: {
    readonly startDate: Date;
    readonly endDate: Date;
  };
}

/**
 * 過去データページコンポーネント
 * 盤と期間で検索し、運転期間一覧を表示する
 */
export const HistoryDataPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialPanelId = searchParams.get('panelId');
  const { isMobile } = useResponsive();

  // 盤一覧
  const [panels, setPanels] = useState<readonly PanelBasicInfo[]>([]);
  const [loadingPanels, setLoadingPanels] = useState<boolean>(true);

  // 検索条件（UI層ではプリミティブ値で状態管理）
  const [searchFilter, setSearchFilter] = useState<SearchFilter>({
    panelId: initialPanelId !== null
      ? parseInt(initialPanelId, HISTORY_DATA_CONSTANTS.DECIMAL_BASE)
      : undefined,
    dateRange: {
      startDate: DateFactory.daysAgo(HISTORY_DATA_CONSTANTS.DEFAULT_DATE_RANGE_DAYS),
      endDate: DateFactory.now(),
    },
  });

  // 運転期間一覧
  const [operationPeriods, setOperationPeriods] = useState<readonly OperationPeriod[]>([]);
  const [loadingPeriods, setLoadingPeriods] = useState<boolean>(false);
  const [searchExecuted, setSearchExecuted] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // チャートモーダル
  const [selectedOperationPeriod, setSelectedOperationPeriod] = useState<OperationPeriod | null>(
    null
  );
  const [isChartModalOpen, setIsChartModalOpen] = useState<boolean>(false);

  // 盤リストを取得
  useEffect(() => {
    const fetchPanels = async (): Promise<void> => {
      try {
        setLoadingPanels(true);
        const panelsData = await getAllPanels();
        setPanels(panelsData);
      } catch (fetchError) {
        logger.captureError(fetchError, 'HistoryDataPage.fetchPanels');
        message.error('盤情報の取得に失敗しました');
      } finally {
        setLoadingPanels(false);
      }
    };

    fetchPanels();
  }, []);

  /**
   * 検索条件変更ハンドラ
   */
  const handleFilterChange = useCallback((filterUpdates: Partial<SearchFilter>): void => {
    setSearchFilter((prev) => ({ ...prev, ...filterUpdates }));
  }, []);

  /**
   * 開始日変更ハンドラ
   */
  const handleStartDateChange = useCallback(
    (date: dayjs.Dayjs | null): void => {
      if (date !== null) {
        handleFilterChange({
          dateRange: {
            ...searchFilter.dateRange,
            startDate: date.toDate(),
          },
        });
      }
    },
    [handleFilterChange, searchFilter.dateRange]
  );

  /**
   * 終了日変更ハンドラ
   */
  const handleEndDateChange = useCallback(
    (date: dayjs.Dayjs | null): void => {
      if (date !== null) {
        handleFilterChange({
          dateRange: {
            ...searchFilter.dateRange,
            endDate: date.toDate(),
          },
        });
      }
    },
    [handleFilterChange, searchFilter.dateRange]
  );

  /**
   * 開始日の無効判定（終了日より後の日付を無効化）
   */
  const disabledStartDate = useCallback(
    (current: dayjs.Dayjs): boolean => {
      return current.isAfter(dayjs(searchFilter.dateRange.endDate), 'day');
    },
    [searchFilter.dateRange.endDate]
  );

  /**
   * 終了日の無効判定（開始日より前の日付を無効化）
   */
  const disabledEndDate = useCallback(
    (current: dayjs.Dayjs): boolean => {
      return current.isBefore(dayjs(searchFilter.dateRange.startDate), 'day');
    },
    [searchFilter.dateRange.startDate]
  );

  /**
   * 検索実行ハンドラ
   */
  const handleSearch = useCallback(async (): Promise<void> => {
    if (searchFilter.panelId === undefined) {
      message.warning('盤を選択してください');
      return;
    }

    setLoadingPeriods(true);
    setError(null);
    setSearchExecuted(true);

    try {
      // サービス層にプリミティブ値を渡し、サービス内部でドメインオブジェクトに変換
      const response = await OperationPeriodService.fetchOperationPeriods({
        panelId: searchFilter.panelId,
        startDate: searchFilter.dateRange.startDate,
        endDate: searchFilter.dateRange.endDate,
      });

      setOperationPeriods(response.operationPeriods);
    } catch (searchError) {
      const errorMessage =
        searchError instanceof Error ? searchError.message : '運転期間の検索に失敗しました';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoadingPeriods(false);
    }
  }, [searchFilter]);

  /**
   * チャート表示ハンドラ
   */
  const handleShowChart = useCallback((operationPeriod: OperationPeriod): void => {
    setSelectedOperationPeriod(operationPeriod);
    setIsChartModalOpen(true);
  }, []);

  /**
   * チャートモーダルを閉じるハンドラ
   */
  const handleCloseChartModal = useCallback((): void => {
    setIsChartModalOpen(false);
    setSelectedOperationPeriod(null);
  }, []);

  // 盤オプションを生成
  const panelOptions = useMemo(() => {
    return panels.map((panel) => ({
      value: panel.id,
      label: panel.panelName,
    }));
  }, [panels]);

  if (loadingPanels) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Spin size="large" tip="盤情報を読み込み中..." />
      </div>
    );
  }

  return (
    <div>
      <Card title="検索条件" className="mb-6">
        <Form layout="vertical">
          <Row
            gutter={
              isMobile ? RESPONSIVE_STYLES.gutter.mobile : RESPONSIVE_STYLES.gutter.desktop
            }
          >
            <Col xs={24} sm={12} md={8}>
              <Form.Item label="盤" required>
                <Select
                  style={{ width: '100%' }}
                  placeholder="盤を選択"
                  value={searchFilter.panelId}
                  onChange={(value: number | undefined) =>
                    handleFilterChange({
                      panelId: value,
                    })
                  }
                  options={panelOptions}
                />
              </Form.Item>
            </Col>

            <Col xs={12} sm={6} md={6}>
              <Form.Item label="開始日" required>
                <DatePicker
                  style={{ width: '100%' }}
                  value={dayjs(searchFilter.dateRange.startDate)}
                  onChange={handleStartDateChange}
                  disabledDate={disabledStartDate}
                  allowClear={false}
                />
              </Form.Item>
            </Col>

            <Col xs={12} sm={6} md={6}>
              <Form.Item label="終了日" required>
                <DatePicker
                  style={{ width: '100%' }}
                  value={dayjs(searchFilter.dateRange.endDate)}
                  onChange={handleEndDateChange}
                  disabledDate={disabledEndDate}
                  allowClear={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={4}>
              <Form.Item label=" ">
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  disabled={searchFilter.panelId === undefined}
                  loading={loadingPeriods}
                  block={isMobile}
                >
                  検索
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {error !== null && (
        <Alert
          message="エラー"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {searchExecuted && (
        <Card
          title={
            <>
              <HistoryOutlined style={{ marginRight: 8 }} />
              運転期間一覧
            </>
          }
        >
          <OperationPeriodTable
            operationPeriods={operationPeriods}
            loading={loadingPeriods}
            isMobile={isMobile}
            onShowChart={handleShowChart}
          />
        </Card>
      )}

      <OperationChartModal
        operationPeriod={selectedOperationPeriod}
        open={isChartModalOpen}
        isMobile={isMobile}
        onClose={handleCloseChartModal}
      />
    </div>
  );
};
